import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useTour } from "./TourContext";

/**
 * Floating progress widget so users in a sandbox tour never feel lost.
 * Shows step N of M with a progress bar, prev/next controls, skip, and a
 * collapsible table of contents for jumping between steps.
 */
const TourProgressWidget: React.FC = () => {
  const { state, tours, nextStep, previousStep, goToStep, skipTour } = useTour();
  const [tocOpen, setTocOpen] = useState(false);

  const { active, tourId, stepIndex } = state;
  if (!active || !tourId) return null;

  const def = tours[tourId];
  if (!def || def.steps.length === 0) return null;

  const total = def.steps.length;
  const current = def.steps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / total) * 100);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;

  const widgetStyle: React.CSSProperties = {
    position: "fixed",
    right: 20,
    bottom: 20,
    zIndex: 9997,
    width: 320,
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    padding: "0.9rem 1rem",
    fontSize: "0.85rem"
  };

  const barBg: React.CSSProperties = {
    height: 4,
    borderRadius: 2,
    background: "#e9ecef",
    overflow: "hidden",
    marginTop: "0.5rem"
  };
  const barFill: React.CSSProperties = {
    height: "100%",
    width: `${progress}%`,
    background: "#3f6ad8",
    transition: "width 200ms ease"
  };

  const btnBase: React.CSSProperties = {
    border: "none",
    borderRadius: 6,
    padding: "0.3rem 0.75rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer"
  };
  const primaryBtn: React.CSSProperties = { ...btnBase, background: "#3f6ad8", color: "#fff" };
  const secondaryBtn: React.CSSProperties = {
    ...btnBase,
    background: "transparent",
    color: "#495057",
    border: "1px solid #dee2e6"
  };

  return ReactDOM.createPortal(
    <div style={widgetStyle} data-testid="tour-progress-widget">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong style={{ fontSize: "0.75rem", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Tour Progress
        </strong>
        <button
          onClick={skipTour}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#adb5bd", fontSize: "0.95rem", padding: 0, lineHeight: 1 }}
          aria-label="Exit tour"
          title="Exit tour"
        >
          ✕
        </button>
      </div>

      <div style={{ marginTop: "0.4rem", fontWeight: 600, color: "#212529" }}>
        {current.title}
      </div>
      <div style={{ fontSize: "0.75rem", color: "#868e96", marginTop: 2 }}>
        Step {stepIndex + 1} of {total}
      </div>

      <div style={barBg}>
        <div style={barFill} />
      </div>

      <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.75rem", alignItems: "center" }}>
        <button
          onClick={previousStep}
          disabled={isFirst}
          style={{ ...secondaryBtn, opacity: isFirst ? 0.4 : 1, cursor: isFirst ? "not-allowed" : "pointer" }}
        >
          ← Back
        </button>
        <button onClick={nextStep} style={primaryBtn}>
          {isLast ? "Finish" : "Next →"}
        </button>
        <button
          onClick={() => setTocOpen(o => !o)}
          style={{ ...secondaryBtn, marginLeft: "auto" }}
          aria-expanded={tocOpen}
        >
          {tocOpen ? "Hide steps" : "All steps"}
        </button>
      </div>

      {tocOpen && (
        <ol style={{ marginTop: "0.75rem", paddingLeft: "1.2rem", maxHeight: 220, overflowY: "auto" }}>
          {def.steps.map((s, i) => {
            const isCurrent = i === stepIndex;
            const isDone = i < stepIndex;
            return (
              <li
                key={i}
                onClick={() => goToStep(i)}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 0.4rem",
                  margin: "0 -0.4rem",
                  borderRadius: 4,
                  background: isCurrent ? "#eef2ff" : "transparent",
                  color: isDone ? "#868e96" : "#212529",
                  fontWeight: isCurrent ? 600 : 400,
                  fontSize: "0.8rem"
                }}
              >
                {isDone ? "✓ " : ""}
                {s.title}
              </li>
            );
          })}
        </ol>
      )}
    </div>,
    document.body
  );
};

export default TourProgressWidget;
